'use client';

import { c as _c } from "react/compiler-runtime";
import ProductListing from '@/app/_components/ProductListing';
import Spinner from '@/app/_components/Spinner';

// Column counts at each breakpoint
const BREAKPOINT_COLUMNS = {
  base: 2,
  // mobile default
  md: 3,
  // 768px
  xl: 4,
  // 1280px
  '2xl': 5 // 1536px
};

/**
 * Calculate which products share a row with a sale item at each breakpoint.
 * Returns an array of class strings for each product index.
 */
function getRowSaleClasses(products) {
  const classesPerProduct = products.map(() => []);

  // For each breakpoint, determine which rows have sale items
  Object.entries(BREAKPOINT_COLUMNS).forEach(([breakpoint, cols]) => {
    // Group products into rows
    for (let rowStart = 0; rowStart < products.length; rowStart += cols) {
      const rowEnd = Math.min(rowStart + cols, products.length);

      // Check if any product in this row is on sale
      let rowHasSale = false;
      for (let i = rowStart; i < rowEnd; i++) {
        if (products[i].on_sale == 1) {
          rowHasSale = true;
          break;
        }
      }

      // If row has a sale, add class to all non-sale items in the row
      if (rowHasSale) {
        for (let i = rowStart; i < rowEnd; i++) {
          if (products[i].on_sale != 1) {
            classesPerProduct[i].push(`row-has-sale-${breakpoint}`);
          }
        }
      }
    }
  });
  return classesPerProduct.map(classes => classes.join(' '));
}

/**
 * ProductGrid - Display component for product listings
 *
 * Receives products and display state from parent (CategoryProducts).
 * No data fetching - just renders what it's given.
 */
export default function ProductGrid(t0) {
  const $ = _c(14);
  const {
    products,
    isLoading,
    hasFilters,
    onClearFilters
  } = t0;
  if (isLoading) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <div role="status" className="flex items-center justify-center py-20"><Spinner size="lg" /></div>;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  if (products.length === 0 && hasFilters) {
    let t1;
    if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <p className="text-sm text-light mb-4">No products match your filters.</p>;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    let t2;
    if ($[2] !== onClearFilters) {
      t2 = <div role="status" className="flex flex-col items-center justify-center py-36 rounded-lg border-1 bg-gray-3">{t1}<button onClick={onClearFilters} className="text-sm text-primary hover:underline">Clear all filters</button></div>;
      $[2] = onClearFilters;
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    return t2;
  }
  if (products.length === 0) {
    let t1;
    if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <div className="flex items-center justify-center py-20"><p className="text-sm text-light">No products in this category.</p></div>;
      $[4] = t1;
    } else {
      t1 = $[4];
    }
    return t1;
  }
  let t1;
  if ($[5] !== products) {
    t1 = getRowSaleClasses(products);
    $[5] = products;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  const rowSaleClasses = t1;
  let t2;
  if ($[7] !== products || $[8] !== rowSaleClasses) {
    let t3;
    if ($[10] !== rowSaleClasses) {
      t3 = (product, index) => <ProductListing key={product.sub_product_id || product.product_id} product={product} className={rowSaleClasses[index]} />;
      $[10] = rowSaleClasses;
      $[11] = t3;
    } else {
      t3 = $[11];
    }
    t2 = products.map(t3);
    $[7] = products;
    $[8] = rowSaleClasses;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  let t3;
  if ($[12] !== t2) {
    t3 = <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-x-4 gap-y-6 md:gap-x-8">{t2}</div>;
    $[12] = t2;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  return t3;
}